// errors.js - catch uncaught JS errors + unhandled promise rejections and
// post them to the client_errors Supabase table. Queried from the Supabase
// dashboard by the director. No third-party SDK, no PII beyond what the
// error itself contains.

import { CONFIG } from './config.js';
import { AUTH } from './auth.js';
import { UI } from './ui.js';

export const ERRORS = (() => {

  let _last = 0;
  // Cap to one report every 2 seconds so a loop of failing renders can't
  // hammer Supabase with thousands of rows.
  const RATE_LIMIT_MS = 2000;

  function _send(payload) {
    const now = Date.now();
    if (now - _last < RATE_LIMIT_MS) return;
    _last = now;
    try {
      fetch(`${CONFIG.supabase.url}/rest/v1/client_errors`, {
        method: 'POST',
        headers: {
          'apikey':        CONFIG.supabase.anonKey,
          'Authorization': `Bearer ${AUTH.getToken && AUTH.getToken() || CONFIG.supabase.anonKey}`,
          'Content-Type':  'application/json',
          'Prefer':        'return=minimal',
        },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {});
    } catch { /* never let error reporting itself throw */ }
  }

  function _role() {
    if (!AUTH || !AUTH.isLoggedIn || !AUTH.isLoggedIn()) return null;
    if (AUTH.isDirector())  return 'director';
    if (AUTH.isSelection()) return 'selection';
    if (AUTH.isCoach())     return 'coach';
    if (AUTH.isFeedback())  return 'feedback';
    return null;
  }

  function _screen() {
    try { return (UI && UI.currentScreen && UI.currentScreen()) || null; }
    catch { return null; }
  }

  function init() {
    window.addEventListener('error', (e) => {
      _send({
        role:       _role(),
        screen:     _screen(),
        message:    (e.error && e.error.message) || e.message || 'unknown',
        stack:      e.error && e.error.stack    || null,
        user_agent: navigator.userAgent,
        url:        location.href,
        extra:      { filename: e.filename, lineno: e.lineno, colno: e.colno },
      });
    });
    window.addEventListener('unhandledrejection', (e) => {
      const reason = e.reason || {};
      _send({
        role:       _role(),
        screen:     _screen(),
        message:    reason.message || String(reason),
        stack:      reason.stack || null,
        user_agent: navigator.userAgent,
        url:        location.href,
        extra:      { kind: 'unhandled_rejection' },
      });
    });
  }

  // Explicit logger for caught-but-noteworthy paths (e.g. catch blocks that
  // toast an error to the user - call this too if you want it persisted).
  function report(message, extra = {}) {
    _send({
      role:       _role(),
      screen:     _screen(),
      message:    String(message || 'manual_report'),
      stack:      null,
      user_agent: navigator.userAgent,
      url:        location.href,
      extra,
    });
  }

  return { init, report };

})();
